import {
  type ForwardRefExoticComponent,
  Fragment,
  type ReactNode,
  forwardRef,
  isValidElement,
} from 'react';

import { PropertyCardActionArea } from './property-card-action-area';
import { PropertyCardBadge } from './property-card-badge';
import { PropertyCardBadgeArea } from './property-card-badge-area';
import { PropertyCardBody } from './property-card-body';
import { PropertyCardDataArea } from './property-card-data-area';
import { PropertyCardDataWrapper } from './property-card-data-wrapper';
import { PropertyCardFlexArea } from './property-card-flex-area';
import { PropertyCardHomeDetails } from './property-card-home-details';
import { PropertyCardMenuTrigger } from './property-card-menu-trigger';
import { PropertyCardMLSLogo } from './property-card-mls-logo';
import { PropertyCardPhoto } from './property-card-photo';
import { PropertyCardPhotoBody } from './property-card-photo-body';
import { PropertyCardPhotoFallback } from './property-card-photo-fallback';
import { PropertyCardPhotoFooter } from './property-card-photo-footer';
import { PropertyCardPhotoHeader } from './property-card-photo-header';
import { PropertyCardPhotoWrapper } from './property-card-photo-wrapper';
import { PropertyCardRoot } from './property-card-root';
import { PropertyCardSaveArea } from './property-card-save-area';
import { PropertyCardSaveButton } from './property-card-save-button';
import type { PropertyCardPropsInterface } from './types/property-card.types';

/**
 * @related ['Carousel', 'PhotoCarousel']
 * @subcomponents ['PropertyCard.Badge', 'PropertyCard.DataArea', 'PropertyCard.HomeDetails',
 * 'PropertyCard.Photo', 'PropertyCard.PhotoFallback', 'PhotoCarousel']
 * @since 8.63.0
 */

export const PropertyCard = forwardRef<HTMLElement, PropertyCardPropsInterface>(
  (props: PropertyCardPropsInterface, forwardedRef) => {
    const {
      actionButton,
      appearance = 'large',
      asChild = false,
      badge,
      elevated = true,
      data,
      flexArea,
      interactive = true,
      loading,
      onClick,
      photoBody,
      photoFooter,
      saveButton,
      tabIndex,
      ...rest
    } = props;

    const getDataAreaProps = (
      dataType: 'dataArea1' | 'dataArea2' | 'dataArea3' | 'dataArea4' | 'dataArea5',
    ) => {
      // If value is a props object, use it as-is.
      // Else, it's a node so assign it to `children` in a new props object.
      const dataAreaObj =
        data !== undefined &&
        data !== null &&
        data[dataType] !== null &&
        typeof data[dataType] === 'object' &&
        !Array.isArray(data[dataType]) &&
        !isValidElement(data[dataType]) &&
        'children' in data[dataType]
          ? data[dataType]
          : { children: data && data[dataType] };

      // If Large card, attach dataArea4 to the end of dataArea2
      if (dataType === 'dataArea2' && props.appearance === 'large' && data?.dataArea4) {
        const dataArea2Children = (dataAreaObj as any).children;

        let dataArea4Children;
        const dataArea4 = data.dataArea4;
        if (typeof dataArea4 === 'object' && typeof (dataArea4 as any).children !== 'undefined') {
          dataArea4Children = (dataArea4 as any).children;
        } else {
          dataArea4Children = dataArea4;
        }

        return {
          ...dataAreaObj,
          children: (
            <Fragment>
              {dataArea2Children}
              <span>{dataArea4Children}</span>
            </Fragment>
          ),
        };
      }

      return dataAreaObj;
    };

    const dataArea5Area = () => {
      const dataArea5Props: Record<string, unknown> & { children: ReactNode } = {
        children: null,
        ...(getDataAreaProps('dataArea5') as object),
      };

      const mlsAttribution = data?.mls?.attribution || '';
      const separator = dataArea5Props.children && mlsAttribution ? '. ' : '';

      if (typeof dataArea5Props.children === 'undefined' && mlsAttribution === '') {
        return null;
      }

      return (
        <PropertyCardDataArea dataType="dataArea5" {...dataArea5Props}>
          {dataArea5Props.children}
          {separator}
          {mlsAttribution}
        </PropertyCardDataArea>
      );
    };

    const mlsLogo = data?.mls?.logoSrc ? (
      <PropertyCardMLSLogo logoSrc={data?.mls?.logoSrc} attribution={data?.mls?.attribution} />
    ) : null;

    const content: ReactNode = loading ? (
      <PropertyCardBody>
        <PropertyCardDataWrapper>
          <PropertyCardDataArea dataType="dataArea1">&nbsp;</PropertyCardDataArea>
          <PropertyCardDataArea dataType="dataArea2">&nbsp;</PropertyCardDataArea>
          <PropertyCardDataArea dataType="dataArea3">&nbsp;</PropertyCardDataArea>
        </PropertyCardDataWrapper>
        <PropertyCardPhotoWrapper>
          <PropertyCardPhotoBody />
        </PropertyCardPhotoWrapper>
      </PropertyCardBody>
    ) : (
      <Fragment>
        <PropertyCardBody
          elevated={elevated}
          interactive={interactive || onClick ? true : undefined}
          onClick={onClick}
          tabIndex={tabIndex}
        >
          <PropertyCardDataWrapper>
            <PropertyCardDataArea
              dataType="dataArea1"
              {...(getDataAreaProps('dataArea1') as object)}
            />
            <PropertyCardDataArea
              dataType="dataArea2"
              {...(getDataAreaProps('dataArea2') as object)}
            />
            <PropertyCardDataArea
              dataType="dataArea3"
              {...(getDataAreaProps('dataArea3') as object)}
            />
            {/* Except for Large card, dataArea4 appears here */}
            {data?.dataArea4 && appearance !== 'large' && (
              <PropertyCardDataArea
                dataType="dataArea4"
                {...(getDataAreaProps('dataArea4') as object)}
              />
            )}
            {/* In Large cards, dataArea5 appears here */}
            {appearance === 'large' && dataArea5Area()}
            <PropertyCardActionArea>{actionButton}</PropertyCardActionArea>
            {/* In a Horizontal Card, MLS logo appears alongside dataArea4 */}
            {appearance === 'horizontal' && mlsLogo}
          </PropertyCardDataWrapper>
          <PropertyCardPhotoWrapper>
            <PropertyCardPhotoHeader>
              <PropertyCardBadgeArea>{badge}</PropertyCardBadgeArea>
              <PropertyCardSaveArea>{saveButton}</PropertyCardSaveArea>
            </PropertyCardPhotoHeader>
            <PropertyCardPhotoBody>{photoBody}</PropertyCardPhotoBody>
            <PropertyCardPhotoFooter>
              {/* In Large and Small cards, MLS logo appears on the photo */}
              {photoFooter || (appearance !== 'horizontal' && mlsLogo)}
            </PropertyCardPhotoFooter>
          </PropertyCardPhotoWrapper>
          {flexArea && <PropertyCardFlexArea>{flexArea}</PropertyCardFlexArea>}
        </PropertyCardBody>

        {/* In Small and Horizontal Cards, dataArea5 appears outside of the card body. */}
        {(appearance === 'small' || appearance === 'horizontal') && dataArea5Area()}
      </Fragment>
    );

    return (
      <PropertyCard.Root
        ref={forwardedRef}
        appearance={appearance}
        asChild={asChild}
        loading={loading}
        {...rest}
      >
        {content}
      </PropertyCard.Root>
    );
  },
) as ForwardRefExoticComponent<PropertyCardPropsInterface> & {
  ActionArea: typeof PropertyCardActionArea;
  Badge: typeof PropertyCardBadge;
  BadgeArea: typeof PropertyCardBadgeArea;
  Body: typeof PropertyCardBody;
  DataArea: typeof PropertyCardDataArea;
  DataWrapper: typeof PropertyCardDataWrapper;
  FlexArea: typeof PropertyCardFlexArea;
  HomeDetails: typeof PropertyCardHomeDetails;
  MenuTrigger: typeof PropertyCardMenuTrigger;
  MLSLogo: typeof PropertyCardMLSLogo;
  Photo: typeof PropertyCardPhoto;
  PhotoBody: typeof PropertyCardPhotoBody;
  PhotoFallback: typeof PropertyCardPhotoFallback;
  PhotoFooter: typeof PropertyCardPhotoFooter;
  PhotoHeader: typeof PropertyCardPhotoHeader;
  PhotoWrapper: typeof PropertyCardPhotoWrapper;
  Root: typeof PropertyCardRoot;
  SaveArea: typeof PropertyCardSaveArea;
  SaveButton: typeof PropertyCardSaveButton;
};

PropertyCard.displayName = 'PropertyCard';

PropertyCard.ActionArea = PropertyCardActionArea;
PropertyCard.Badge = PropertyCardBadge;
PropertyCard.BadgeArea = PropertyCardBadgeArea;
PropertyCard.Body = PropertyCardBody;
PropertyCard.DataArea = PropertyCardDataArea;
PropertyCard.DataWrapper = PropertyCardDataWrapper;
PropertyCard.FlexArea = PropertyCardFlexArea;
PropertyCard.HomeDetails = PropertyCardHomeDetails;
PropertyCard.MenuTrigger = PropertyCardMenuTrigger;
PropertyCard.MLSLogo = PropertyCardMLSLogo;
PropertyCard.Photo = PropertyCardPhoto;
PropertyCard.PhotoBody = PropertyCardPhotoBody;
PropertyCard.PhotoFallback = PropertyCardPhotoFallback;
PropertyCard.PhotoFooter = PropertyCardPhotoFooter;
PropertyCard.PhotoHeader = PropertyCardPhotoHeader;
PropertyCard.PhotoWrapper = PropertyCardPhotoWrapper;
PropertyCard.Root = PropertyCardRoot;
PropertyCard.SaveArea = PropertyCardSaveArea;
PropertyCard.SaveButton = PropertyCardSaveButton;
